import React, { useState, useEffect } from 'react';
import { Mail, Phone, Calendar, Clock, Tag, User, MessageSquare, Trash2, ChevronDown } from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from 'sonner';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New Lead', color: 'bg-blue-100 text-blue-800' },
  { value: 'follow_up_scheduled', label: 'Follow-Up Scheduled', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'follow_up_sent', label: 'Follow-Up Sent', color: 'bg-purple-100 text-purple-800' },
  { value: 'booking_started', label: 'Booking Started', color: 'bg-cyan-100 text-cyan-800' },
  { value: 'booked', label: 'Booked', color: 'bg-green-100 text-green-800' },
  { value: 'contacted_admin', label: 'Contacted Admin', color: 'bg-amber-100 text-amber-800' },
  { value: 'lost', label: 'Lost Lead', color: 'bg-gray-100 text-gray-600' },
];

const FOLLOWUP_LABELS = {
  not_scheduled: { label: 'Not Scheduled', color: 'text-gray-500' },
  scheduled: { label: 'Scheduled', color: 'text-yellow-600' },
  sent: { label: 'Sent', color: 'text-green-600' },
  skipped_booked: { label: 'Skipped (Booked)', color: 'text-blue-600' },
  skipped_no_email: { label: 'Skipped (No Email)', color: 'text-gray-500' },
  skipped_contacted_admin: { label: 'Skipped (Contacted)', color: 'text-amber-600' },
};

const ManageLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedLead, setExpandedLead] = useState(null);

  useEffect(() => {
    loadLeads();
  }, []);

  const loadLeads = async () => {
    try {
      const res = await api.getLeads();
      setLeads(res.data);
    } catch {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (leadId, status) => {
    try {
      await api.updateLeadStatus(leadId, status);
      toast.success('Lead status updated');
      loadLeads();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm('Delete this lead permanently?')) return;
    try {
      await api.deleteLead(leadId);
      toast.success('Lead deleted');
      loadLeads();
    } catch {
      toast.error('Failed to delete lead');
    }
  };

  const getStatusBadge = (status) => {
    const opt = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${opt.color}`}>{opt.label}</span>;
  };

  const getFollowupBadge = (status) => {
    const info = FOLLOWUP_LABELS[status] || FOLLOWUP_LABELS.not_scheduled;
    return <span className={`text-xs font-medium ${info.color}`}>{info.label}</span>;
  };

  const filteredLeads = filter === 'all' ? leads : leads.filter(l => l.lead_status === filter);

  const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-4 border-[#3E2723] border-t-transparent"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="font-serif text-2xl text-[#3E2723]" data-testid="leads-title">Chatbot Leads</h2>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-[#786E66]">Filter:</span>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border border-[#E6DCD3] rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-[#3E2723] outline-none"
            data-testid="leads-filter"
          >
            <option value="all">All ({leads.length})</option>
            {STATUS_OPTIONS.map(s => (
              <option key={s.value} value={s.value}>
                {s.label} ({leads.filter(l => l.lead_status === s.value).length})
              </option>
            ))}
          </select>
        </div>
      </div>

      {filteredLeads.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-[#E6DCD3]">
          <MessageSquare className="h-12 w-12 text-[#E6DCD3] mx-auto mb-4" />
          <p className="text-[#786E66]">No leads captured yet. Leads from the AI chatbot will appear here.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredLeads.map((lead) => (
            <div key={lead.id} className="bg-white rounded-xl border border-[#E6DCD3] overflow-hidden" data-testid={`lead-card-${lead.id}`}>
              {/* Lead Header */}
              <div
                className="p-5 flex items-center justify-between cursor-pointer hover:bg-[#FCFBF9] transition-colors"
                onClick={() => setExpandedLead(expandedLead === lead.id ? null : lead.id)}
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="h-10 w-10 rounded-full bg-[#F3EBE1] flex items-center justify-center flex-shrink-0">
                    <User className="h-5 w-5 text-[#3E2723]" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-medium text-[#1A1A1A] truncate">{lead.name}</h3>
                    <div className="flex items-center gap-3 text-xs text-[#786E66] mt-0.5">
                      {lead.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{lead.email}</span>}
                      {lead.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{lead.phone}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {getStatusBadge(lead.lead_status)}
                  <ChevronDown className={`h-4 w-4 text-[#786E66] transition-transform ${expandedLead === lead.id ? 'rotate-180' : ''}`} />
                </div>
              </div>

              {/* Expanded Details */}
              {expandedLead === lead.id && (
                <div className="border-t border-[#E6DCD3] p-5 bg-[#FCFBF9] space-y-4">
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-[#786E66] flex items-center gap-1 mb-1"><Tag className="h-3.5 w-3.5" />Service Interest</span>
                      <p className="text-[#1A1A1A] font-medium">{lead.service_interest || 'General inquiry'}</p>
                    </div>
                    <div>
                      <span className="text-[#786E66] flex items-center gap-1 mb-1"><Calendar className="h-3.5 w-3.5" />Preferred Date</span>
                      <p className="text-[#1A1A1A] font-medium">{lead.preferred_date || 'Not specified'}</p>
                    </div>
                    <div>
                      <span className="text-[#786E66] flex items-center gap-1 mb-1"><Clock className="h-3.5 w-3.5" />Urgency</span>
                      <p className={`font-medium ${lead.urgency === 'high' ? 'text-red-600' : 'text-[#1A1A1A]'}`}>
                        {lead.urgency === 'high' ? 'High' : 'Normal'}
                      </p>
                    </div>
                    <div>
                      <span className="text-[#786E66] mb-1 block">Lead Source</span>
                      <p className="text-[#1A1A1A] font-medium">{lead.lead_source}</p>
                    </div>
                    <div>
                      <span className="text-[#786E66] mb-1 block">Follow-Up Email</span>
                      {getFollowupBadge(lead.followup_status)}
                      {lead.followup_sent_at && (
                        <p className="text-xs text-[#786E66] mt-0.5">Sent: {formatDate(lead.followup_sent_at)}</p>
                      )}
                    </div>
                    <div>
                      <span className="text-[#786E66] mb-1 block">Captured</span>
                      <p className="text-[#1A1A1A] text-xs">{formatDate(lead.created_at)}</p>
                    </div>
                  </div>

                  {lead.conversation_summary && (
                    <div>
                      <span className="text-[#786E66] text-sm flex items-center gap-1 mb-2"><MessageSquare className="h-3.5 w-3.5" />Conversation Summary</span>
                      <pre className="text-xs text-[#1A1A1A] bg-white rounded-lg border border-[#E6DCD3] p-3 whitespace-pre-wrap font-sans">{lead.conversation_summary}</pre>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-2 border-t border-[#E6DCD3]">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#786E66]">Update status:</span>
                      <select
                        value={lead.lead_status}
                        onChange={(e) => updateStatus(lead.id, e.target.value)}
                        className="border border-[#E6DCD3] rounded-lg px-2 py-1 text-xs focus:ring-2 focus:ring-[#3E2723] outline-none"
                        data-testid={`lead-status-select-${lead.id}`}
                      >
                        {STATUS_OPTIONS.map(s => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      onClick={() => deleteLead(lead.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors"
                      title="Delete lead"
                      data-testid={`delete-lead-${lead.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ManageLeads;
